import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Activity, AlertTriangle, Archive, ArrowLeft, Bed, ChevronLeft, ChevronRight, CircleHelp, createLucideIcon, CreditCard, Download, Droplet, Dumbbell, ExternalLink,
  FileText, FolderOpen, Heart, HeartPulse, Phone, Pill, Plus,
  Calendar as CalendarDays, CloudDownload, Printer, Ruler, ShieldAlert, Smile, Stethoscope, Thermometer, Trash2, Users, Utensils, Weight, Wind
} from 'lucide-react'
import { patientService } from '../../../services/patientApi'
import { useAuth } from '../../../contexts/AuthContext'
import { ACTIONS, MODULES, usePermissions } from '../../../hooks/usePermissions'
import { downloadPatientVitals, printPatientEvolutions, printPatientRecord, printPatientVitals } from './patientRecordPrint'
import Calendar, { CalendarRangePicker } from '../../../components/ERP/Calendar/Calendar'
import { toDateInputValue } from '../../../utils/dateUtils'
import {
  CHARGE_CATEGORIES, CHARGE_STATUSES, STATUS_LABELS,
  ageFromBirth, formatDateTime, formatDay, formatMoney, genderLabel, labelOf
} from './patientFormat'
import AddVitalSignsModal from './AddVitalSignsModal'
import VitalHistoryTable from './VitalHistoryTable'
import NutritionWaterButton from './Nutrition/NutritionWaterButton'
import { todayInputValue } from './Nutrition/nutritionFormat'
import AddBodyMeasureModal from './AddBodyMeasureModal'
import MedicationsTab from './Medications/MedicationsTab'
import AddPaymentModal from './AddPaymentModal'
import AddEvolutionModal from './AddEvolutionModal'
import TherapyTab from './Therapy/TherapyTab'
import NutritionTab from './Nutrition/NutritionTab'
import ArchivePatientModal from './ArchivePatientModal'
import NewPatientModal from './NewPatientModal'
import PatientContactsTab from './PatientContactsTab'
import PatientAccessModal from './PatientAccessModal'
import Avatar from '../../../components/Avatar/Avatar'
import PatientShiftStack from './PatientShiftStack'
import DesktopShiftButton from './DesktopShiftButton'
import { PatientRecordSkeleton } from './PatientsSkeletons'
import { downloadDocumentFile } from '../../../utils/downloadDocumentFile'
import { councilStamp } from '../../../utils/professionalCouncil'
import StickyNotePlus from '../../../components/ERP/icons/StickyNotePlus'
import ShiftNoteSheet from './ShiftNoteSheet'
import NotesTab from './NotesTab'
import SquareText from '../../../components/ERP/icons/SquareText'
import OnboardingTour from '../../../components/ERP/Onboarding/OnboardingTour'
import { CHART_ONBOARDING_STEPS, CHART_TOUR_ID } from './chartOnboarding'
import '../Financeiro/Dashboard/FinanceDashboardPage.css'
import './PatientsPage.css'

function clinicalFileName(patientName, kind) {
  const name = String(patientName || 'Paciente')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  const date = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' })
    .format(new Date())
    .replace(/\//g, '-')
  const label = kind === 'evolutions' ? 'evolucoes' : 'prontuario'
  return `${name} ${date} ${label}.pdf`
}

function badgeClass(status) {
  return `patients-badge patients-badge--${status || 'neutral'}`
}

const Pen = createLucideIcon('pen', [
  ['path', { d: 'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z' }],
  ['path', { d: 'm15 5 4 4' }]
])

function evolutionDay(value) {
  return String(value || '').slice(0, 10)
}

function dayInPeriod(day, from, to) {
  if (!from && !to) return true
  if (!day) return false
  if (from && !to) return day === from
  if (from && day < from) return false
  if (to && day > to) return false
  return true
}

function evolutionInPeriod(item, from, to) {
  return dayInPeriod(evolutionDay(item.evolution_date), from, to)
}

function vitalDay(value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value || '').slice(0, 10)
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date)
}

function evolutionPeriodLabel(from, to, allLabel = 'Todas as evoluções') {
  if (!from && !to) return allLabel
  if (from && (!to || from === to)) return formatDay(from)
  if (from && to) return `${formatDay(from)} a ${formatDay(to)}`
  return formatDay(to)
}

function evolutionBounds(start, end) {
  const from = start ? toDateInputValue(start) : ''
  const to = end ? toDateInputValue(end) : from
  return { from, to }
}

function latestReading(rows, keys) {
  return rows.find((item) => keys.some((key) => item[key] != null && item[key] !== '')) || null
}

function formatBody(value, digits) {
  const number = Number(value)
  if (!Number.isFinite(number)) return '—'
  return number.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function PatientTabBar({ tabs, currentTab, onSelect }) {
  const scrollerRef = useRef(null)
  const gooId = `patients-tabs-goo-${useId().replace(/:/g, '')}`
  const [scroll, setScroll] = useState({ overflow: false, progress: 0, pages: 0, atStart: true, atEnd: true })
  const tabsKey = tabs.map((tab) => tab.id).join('|')

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return undefined
    let frame = 0
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth
      const overflow = max > 8
      const pages = overflow ? Math.min(7, Math.max(2, Math.ceil(el.scrollWidth / el.clientWidth))) : 0
      const progress = max > 0 ? Math.min(1, Math.max(0, el.scrollLeft / max)) : 0
      const atStart = el.scrollLeft <= 8
      const atEnd = el.scrollLeft >= max - 8
      setScroll((prev) => {
        if (
          prev.overflow === overflow
          && prev.pages === pages
          && prev.atStart === atStart
          && prev.atEnd === atEnd
          && Math.abs(prev.progress - progress) < 0.01
        ) return prev
        return { overflow, pages, progress, atStart, atEnd }
      })
    }
    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    el.scrollLeft = 0
    measure()
    el.addEventListener('scroll', onScroll, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener('scroll', onScroll)
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [tabsKey])

  const scrollByStep = (direction) => {
    const el = scrollerRef.current
    if (!el) return
    el.scrollBy({ left: direction * Math.max(180, el.clientWidth * 0.75), behavior: 'smooth' })
  }

  const scrollToPage = (index) => {
    const el = scrollerRef.current
    if (!el || scroll.pages < 2) return
    const max = el.scrollWidth - el.clientWidth
    el.scrollTo({ left: (index / (scroll.pages - 1)) * max, behavior: 'smooth' })
  }

  const activePage = scroll.pages > 1 ? Math.round(scroll.progress * (scroll.pages - 1)) : 0

  return (
    <div className={`patients-tabs-wrap${scroll.overflow ? ' has-nav' : ''}`} data-onboarding="chart-tabs">
      {scroll.overflow && (
        <button
          type="button"
          className="patients-tabs-arrow patients-tabs-arrow--prev"
          aria-label="Anterior"
          disabled={scroll.atStart}
          onClick={() => scrollByStep(-1)}
        >
          <ChevronLeft size={16} />
        </button>
      )}
      <div className="patients-tabs" ref={scrollerRef}>
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isActive = currentTab === tab.id
          return (
            <button key={tab.id} type="button" onClick={() => onSelect(tab.id)} className={`patients-tab${isActive ? ' is-active' : ''}`}>
              <Icon size={15} />
              <span>{tab.label}</span>
              {tab.count !== undefined && <span className="patients-tab-count">{tab.count}</span>}
            </button>
          )
        })}
      </div>
      {scroll.overflow && (
        <button
          type="button"
          className="patients-tabs-arrow patients-tabs-arrow--next"
          aria-label="Próximo"
          disabled={scroll.atEnd}
          onClick={() => scrollByStep(1)}
        >
          <ChevronRight size={16} />
        </button>
      )}
      {scroll.overflow && (
        <div className="patients-tabs-dots">
          <svg className="patients-tabs-goo-def" aria-hidden="true" focusable="false">
            <filter id={gooId} x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="2.6" result="blur" />
              <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -8" result="goo" />
              <feBlend in="SourceGraphic" in2="goo" />
            </filter>
          </svg>
          <div
            className="patients-tabs-dots-track"
            style={{ '--progress': scroll.progress, filter: `url(#${gooId})` }}
          >
            {Array.from({ length: scroll.pages }, (_, index) => (
              <button
                key={index}
                type="button"
                className={`patients-tabs-dot${index === activePage ? ' is-active' : ''}`}
                aria-label={`Trecho ${index + 1} de ${scroll.pages}`}
                aria-current={index === activePage ? 'true' : undefined}
                onClick={() => scrollToPage(index)}
              />
            ))}
            <span className="patients-tabs-glide" />
          </div>
        </div>
      )}
    </div>
  )
}

function bodyMassIndex(peso, altura) {
  const weight = Number(peso?.value)
  const height = Number(altura?.value)
  if (!weight || !height) return null
  return weight / (height * height)
}

function initials(name) {
  return String(name || '?').split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
}

function PatientRecordPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { organizations, currentSchema, user } = useAuth()
  const { hasPermission } = usePermissions()
  const canUpdate = hasPermission(MODULES.PATIENTS, ACTIONS.UPDATE)
  const canManageAccess = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_MANAGE_ACCESS)
  const canDeletePatient = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_DELETE_PATIENT)
  const canArchive = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_ARCHIVE)
  const canVitals = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_VITALS)
  const canRegisterWeight = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_VITALS_WEIGHT)
  const canRegisterHeight = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_VITALS_HEIGHT)
  const canRegisterVitals = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_VITALS_RECORD)
  const canEditVitals = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_VITALS_EDIT)
  const canSeeVitalShortcut = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_VITALS_SHORTCUT)
  const canMedications = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_MEDICATIONS)
  const canPrescribe = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_MEDICATIONS_PRESCRIBE)
  const canAdminister = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_MEDICATIONS_ADMINISTER)
  const canContacts = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_CONTACTS)
  const canEditContacts = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_CONTACTS_EDIT)
  const canFinancial = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_FINANCIAL)
  const canEvolutions = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_EVOLUTIONS)
  const canRecordNotes = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NOTES_RECORD)
  const canNotes = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NOTES) || canRecordNotes
  const canPrintEvolutions = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_EVOLUTIONS_PRINT)
  const canDocuments = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_DOCUMENTS)
  const canTherapy = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_THERAPY)
  const canRecordWater = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NUTRITION_WATER)
  const canNutrition = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NUTRITION)
  const canNutritionPrescriptions = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NUTRITION_PRESCRIPTIONS)
  const canCreateNutritionPrescriptions = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NUTRITION_PRESCRIPTIONS_CREATE)
  const canRecordMeal = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_NUTRITION_RECORD)
  const canPrintChart = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_CHART_PRINT)
  const canEditChart = hasPermission(MODULES.PATIENTS, ACTIONS.PATIENTS_EDIT_CHART)
  const photoInput = useRef(null)
  const loadSeq = useRef(0)
  const [chart, setChart] = useState(null)
  const [missing, setMissing] = useState(false)
  const [error, setError] = useState('')
  const [shiftState, setShiftState] = useState({ required: false, open: null })
  const [shiftBlocked, setShiftBlocked] = useState(false)
  const [shiftBlockMessage, setShiftBlockMessage] = useState('')
  const rememberShift = useCallback((next) => setShiftState(next), [])
  const shiftLinkedHere = Boolean(shiftState.open && String(shiftState.open.patient_id) === String(id))
  const [activeTab, setActiveTab] = useState('vitals')
  const [vitalOpen, setVitalOpen] = useState(false)
  const [measureKind, setMeasureKind] = useState(null)
  const [evolutionFrom, setEvolutionFrom] = useState('')
  const [evolutionTo, setEvolutionTo] = useState('')
  const [evolutionCalendarOpen, setEvolutionCalendarOpen] = useState(false)
  const [vitalFrom, setVitalFrom] = useState('')
  const [vitalTo, setVitalTo] = useState('')
  const [vitalCalendarOpen, setVitalCalendarOpen] = useState(false)
  const evolutionCalendarRef = useRef(null)
  const vitalCalendarRef = useRef(null)
  const [prescribeTick, setPrescribeTick] = useState(0)
  const [paymentOpen, setPaymentOpen] = useState(false)
  const [evolutionOpen, setEvolutionOpen] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [tourReplay, setTourReplay] = useState(0)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [accessOpen, setAccessOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [documentTitle, setDocumentTitle] = useState('')
  const [documentFile, setDocumentFile] = useState(null)
  const documentInput = useRef(null)
  const [trackedId, setTrackedId] = useState(id)
  if (trackedId !== id) {
    setTrackedId(id)
    setChart(null)
    setMissing(false)
    setError('')
    setEvolutionFrom('')
    setEvolutionTo('')
    setActiveTab('vitals')
  }

  useEffect(() => {
    const content = document.querySelector('.erp-content')
    if (content) content.scrollTop = 0
    window.scrollTo(0, 0)
  }, [id])

  const loadPatient = useCallback(async () => {
    if (!id) return
    const seq = ++loadSeq.current
    setError('')
    try {
      const response = await patientService.getById(id)
      if (seq !== loadSeq.current) return
      setChart(response.data)
      setMissing(false)
    } catch (err) {
      if (seq !== loadSeq.current) return
      if (err.response?.status === 404) setMissing(true)
      else setError(err.response?.data?.message || 'Não foi possível abrir o prontuário')
    }
  }, [id])

  useEffect(() => {
    setChart(null)
    setMissing(false)
    loadPatient()
  }, [loadPatient])

  useEffect(() => {
    const refresh = () => loadPatient()
    window.addEventListener('wfmed-shift-updated', refresh)
    return () => window.removeEventListener('wfmed-shift-updated', refresh)
  }, [loadPatient])

  useEffect(() => {
    if (!shiftState.open) setShiftBlockMessage('')
    if (shiftLinkedHere) {
      setShiftBlocked(false)
      setShiftBlockMessage('')
    }
  }, [shiftLinkedHere, shiftState.open])

  useEffect(() => {
    const onBlocked = (event) => {
      setShiftBlocked(true)
      setShiftBlockMessage(event?.detail?.message || '')
      setError('')
    }
    window.addEventListener('wfmed-shift-blocked', onBlocked)
    return () => window.removeEventListener('wfmed-shift-blocked', onBlocked)
  }, [])

  const guardRecord = (event) => {
    if (!shiftState.required || shiftLinkedHere || shiftBlocked) return
    const target = event.target.closest('button, input[type="submit"]')
    if (!target) return
    if (target.closest('.patients-shift, .patients-tab, .patients-tabs-arrow, .patients-tabs-dot, .finance-dashboard-period-btn, .patients-photo-btn, .patients-tour-help')) return
    event.preventDefault()
    event.stopPropagation()
    setShiftBlockMessage(shiftState.open
      ? `Seu plantão está vinculado a ${shiftState.open.patient_name || 'outro paciente'}.`
      : '')
    setShiftBlocked(true)
  }

  const run = async (action) => {
    setError('')
    try {
      await action()
      await loadPatient()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível atualizar o prontuário')
    }
  }

  if (!chart || String(chart.id) !== String(id)) {
    if (chart || (!missing && !error)) {
      return <PatientRecordSkeleton onBack={() => navigate('/erp/pacientes')} />
    }
    return (
      <div className="finance-dashboard patients-module">
        <div className="patients-empty">
          <HeartPulse size={40} />
          <h3>{missing ? 'Paciente não encontrado' : 'Não foi possível abrir o prontuário'}</h3>
          <p className="patients-muted">{error || 'O registro solicitado não consta na base de dados.'}</p>
          <button type="button" onClick={() => navigate('/erp/pacientes')} className="patients-btn patients-btn--primary" style={{ marginTop: '1rem' }}>
            Voltar para a lista de pacientes
          </button>
        </div>
      </div>
    )
  }

  const allergies = chart.allergies || []
  const alerts = chart.alerts || []
  const vitals = chart.vitals || []
  const activePrescriptions = Number(chart.active_prescription_count || 0)
  const charges = chart.charges || []
  const evolutions = chart.evolutions || []
  const documents = chart.documents || []
  const archived = Boolean(chart.archived_at)
  const archiveLabel = chart.archive_reason === 'obito' ? 'Óbito' : 'Saída'
  const latest = latestReading(vitals, ['systolic', 'diastolic', 'heart_rate', 'oxygen_saturation', 'temperature', 'respiratory_rate', 'pain_scale'])
  const latestGlucose = latestReading(vitals, ['blood_glucose'])
  const measures = chart.measures || {}
  const peso = measures.peso
  const altura = measures.altura
  const imc = bodyMassIndex(peso, altura)
  const age = ageFromBirth(chart.birth_date)
  const totalBilled = charges.reduce((sum, item) => sum + Number(item.amount || 0), 0)
  const totalPaid = charges.filter((item) => item.status === 'pago').reduce((sum, item) => sum + Number(item.amount || 0), 0)
  const totalPending = charges.filter((item) => item.status === 'pendente' || item.status === 'em_auditoria').reduce((sum, item) => sum + Number(item.amount || 0), 0)
  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    await run(() => patientService.addPhoto(id, file))
  }

  const tabs = [
    (canVitals || canRegisterVitals) && { id: 'vitals', label: 'Sinais vitais', count: vitals.length, icon: Activity },
    canMedications && { id: 'medications', label: 'Medicamentos', count: activePrescriptions, icon: Pill },
    (canNutrition || canRecordWater) && { id: 'nutrition', label: 'Alimentação', icon: Utensils },
    canContacts && { id: 'contacts', label: 'Contatos', icon: Phone },
    canFinancial && { id: 'payments', label: 'Financeiro', count: charges.length, icon: CreditCard },
    canEvolutions && { id: 'evolutions', label: 'Evoluções', count: evolutions.length, icon: SquareText },
    canNotes && { id: 'notes', label: 'Diário', icon: StickyNotePlus },
    canTherapy && { id: 'therapy', label: 'Reabilitação', icon: Dumbbell },
    canDocuments && { id: 'documents', label: 'Documentação', count: documents.length, icon: FolderOpen }
  ].filter(Boolean)
  const currentTab = tabs.some((tab) => tab.id === activeTab) ? activeTab : (tabs[0]?.id || '')

  const removePatient = async () => {
    const confirmed = window.confirm(`Ocultar o prontuário de ${chart.name} da lista? Os dados permanecem guardados no sistema.`)
    if (!confirmed) return
    setError('')
    try {
      await patientService.remove(id)
      navigate('/erp/pacientes')
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível excluir o paciente')
    }
  }

  const printOptions = () => {
    const schema = currentSchema || user?.organization_schema
    const organization = (organizations || []).find((item) => item.schema_name === schema)
    return {
      clinicName: organization?.label || user?.organization_label || '',
      issuedBy: user?.name || '',
      signatureUrl: user?.signature_url || '',
      councilLine: councilStamp(user)
    }
  }

  const openClinicalPdf = async (kind, extra = {}, mode = 'open') => {
    const options = printOptions()
    try {
      const response = await patientService.clinicalDocument(id, {
        kind,
        clinicName: options.clinicName,
        ...extra
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      if (mode === 'download') {
        const link = document.createElement('a')
        link.href = url
        link.download = clinicalFileName(chart.name, kind)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 1000)
        setError('')
        return
      }
      const opened = window.open(url, '_blank')
      if (!opened) {
        setError('Permita pop-ups do navegador para abrir o documento.')
        return
      }
      setError('')
    } catch (err) {
      let message = 'Não foi possível gerar o PDF assinado.'
      const data = err.response?.data
      if (data instanceof Blob) {
        try {
          const payload = JSON.parse(await data.text())
          if (payload.message) message = payload.message
        } catch (parseError) {
          message = err.response?.data?.message || message
        }
      } else if (err.response?.data?.message) {
        message = err.response.data.message
      }
      const opened = kind === 'evolutions'
        ? printPatientEvolutions(chart, { ...options, evolutions: visibleEvolutions, periodLabel: evolutionRangeLabel })
        : printPatientRecord(chart, options)
      setError(opened ? `${message} A folha foi aberta com a assinatura do perfil.` : message)
    }
  }

  const printChart = () => {
    openClinicalPdf('record')
  }

  const downloadChart = () => {
    openClinicalPdf('record', {}, 'download')
  }

  const visibleEvolutions = evolutions.filter((item) => evolutionInPeriod(item, evolutionFrom, evolutionTo))
  const evolutionRangeLabel = evolutionPeriodLabel(evolutionFrom, evolutionTo)
  const visibleVitals = vitals.filter((item) => dayInPeriod(vitalDay(item.recorded_at), vitalFrom, vitalTo))
  const vitalRangeLabel = evolutionPeriodLabel(vitalFrom, vitalTo, 'Todas as aferições')

  const evolutionDocumentQuery = () => {
    const query = {
      from: evolutionFrom || '',
      to: evolutionTo || ''
    }
    if (evolutionFrom || evolutionTo) {
      query.ids = visibleEvolutions.map((item) => item.id).join(',') || '0'
    }
    return query
  }

  const printEvolutions = () => {
    openClinicalPdf('evolutions', evolutionDocumentQuery())
  }

  const downloadEvolutions = () => {
    openClinicalPdf('evolutions', evolutionDocumentQuery(), 'download')
  }

  const vitalReportOptions = () => ({
    ...printOptions(),
    vitals: visibleVitals,
    periodLabel: vitalRangeLabel
  })

  const printVitals = () => {
    const opened = printPatientVitals(chart, vitalReportOptions())
    if (!opened) setError('Permita pop-ups do navegador para abrir o documento.')
  }

  const downloadVitals = async () => {
    try {
      await downloadPatientVitals(chart, vitalReportOptions())
    } catch {
      setError('Não foi possível baixar o relatório.')
    }
  }

  const uploadDocument = async (event) => {
    event.preventDefault()
    if (!documentFile || !documentTitle.trim()) return
    setError('')
    try {
      await patientService.addDocument(id, documentFile, documentTitle.trim())
      setDocumentTitle('')
      setDocumentFile(null)
      if (documentInput.current) documentInput.current.value = ''
      await loadPatient()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível enviar o documento')
    }
  }

  return (
    <div className="finance-dashboard patients-module patients-reveal" onClickCapture={guardRecord}>
      <div className={`finance-dashboard-header${shiftState.required ? ' has-shift-action' : ''}`}>
        <div>
          <h1 className="finance-dashboard-title">
            <span className="finance-dashboard-title-highlight">{chart.name}</span>
          </h1>
          <p className="finance-dashboard-subtitle">Prontuário</p>
        </div>
        <div className="finance-dashboard-header-actions">
          <DesktopShiftButton patientId={chart.id} patientName={chart.name} lifted={shiftBlocked} onStatus={rememberShift} />
          {(chart.viewers || []).length > 0 && (
            <div className="patients-access-stack" title="Usuários com acesso">
              {(chart.viewers || []).slice(0, 5).map((user) => (
                <Avatar
                  key={user.id}
                  name={user.name}
                  email={user.email}
                  imageUrl={user.avatar_url}
                  size="sm"
                />
              ))}
              {(chart.viewers || []).length > 5 && (
                <span className="patients-access-more">+{(chart.viewers || []).length - 5}</span>
              )}
            </div>
          )}
          <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact patients-tour-help" onClick={() => setTourReplay((current) => current + 1)}>
            <CircleHelp size={14} /> Como usar
          </button>
          {canManageAccess && (
            <button type="button" onClick={() => setAccessOpen(true)} className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive">
              <Users size={14} /> Acesso
            </button>
          )}
          <button type="button" onClick={() => navigate('/erp/pacientes')} className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive">
            <ArrowLeft size={14} /> Voltar
          </button>
        </div>
      </div>

      {error && (
        <div className="finance-dashboard-error-banner" role="alert">
          <p>{error}</p>
        </div>
      )}

      <section className="patients-panel">
        <div className="patients-profile" data-onboarding="chart-profile">
          <div className="patients-profile-main">
            <div className="patients-avatar-wrap">
              {chart.photo_url
                ? <img src={chart.photo_url} alt="" className="patients-avatar-lg" />
                : <div className="patients-avatar-lg">{initials(chart.name)}</div>}
              {chart.blood_type && <span className="patients-blood patients-blood--lg">{chart.blood_type}</span>}
              {(canUpdate || canEditChart) && (
                <button type="button" onClick={() => photoInput.current?.click()} className="patients-photo-btn">
                  {chart.photo_url ? 'Trocar' : 'Foto'}
                </button>
              )}
              <input ref={photoInput} type="file" accept="image/*" hidden onChange={uploadPhoto} />
            </div>
            <div className="patients-profile-identity">
              <div className="patients-tags">
                <span className={badgeClass(chart.status)}>{STATUS_LABELS[chart.status] || chart.status}</span>
                {archived && <span className="patients-badge patients-badge--alta">Arquivado · {archiveLabel}</span>}
                <span className="patients-muted">Admissão: {formatDay(chart.admission_date)}</span>
              </div>
              <h2 className="patients-name patients-name--lg">{chart.name}</h2>
              <p className="patients-muted">
                {age != null ? `${age} anos` : 'Idade não informada'} ({formatDay(chart.birth_date)}) · Sexo {genderLabel(chart.gender)} · CPF {chart.cpf || '—'} · RG {chart.rg || '—'}
              </p>
              <div className="patients-meta" style={{ border: 'none', paddingTop: '0.45rem' }}>
                <span className="patients-inline"><Bed size={14} className="tone-green" /> {chart.bed || 'Sem leito fixo'}{chart.room_category ? ` (${chart.room_category})` : ''}</span>
                <span className="patients-inline"><CreditCard size={14} className="tone-green" /> {chart.insurance_provider || 'Particular'}{chart.insurance_plan ? ` (${chart.insurance_plan})` : ''}</span>
                <span className="patients-inline"><Stethoscope size={14} /> {chart.attending_doctor_name || 'Médico não informado'}</span>
              </div>
            </div>
            <div className="patients-profile-side">
              {canPrintChart && (
                <div className="patients-profile-print">
                  <button type="button" onClick={downloadChart} className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive">
                    <CloudDownload size={14} /> Baixar
                  </button>
                  <button type="button" onClick={printChart} className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive">
                    <Printer size={14} /> Imprimir
                  </button>
                </div>
              )}
              <PatientShiftStack people={chart.on_shift} />
            </div>
          </div>
          <div className="patients-profile-actions" data-onboarding="chart-actions">
            {canEditChart && (
              <button type="button" onClick={() => setEditOpen(true)} className="patients-btn patients-btn--ghost">
                <Pen size={16} /> Editar prontuário
              </button>
            )}
            {canRegisterVitals && !archived && <button type="button" onClick={() => setVitalOpen(true)} className="patients-btn patients-btn--rose"><Activity size={16} /> Aferir sinais</button>}
            {canPrescribe && !archived && <button type="button" onClick={() => { setActiveTab('medications'); setPrescribeTick((current) => current + 1) }} className="patients-btn patients-btn--primary"><Pill size={16} /> Medicamento</button>}
            {canEvolutions && !archived && <button type="button" data-onboarding="chart-evolution" onClick={() => setEvolutionOpen(true)} className="patients-btn patients-btn--dark"><FileText size={16} /> Nova evolução</button>}
            {canArchive && !archived && <button type="button" onClick={() => setArchiveOpen(true)} className="patients-btn patients-btn--ghost"><Archive size={16} /> Arquivar</button>}
            {canDeletePatient && <button type="button" onClick={removePatient} className="patients-btn patients-btn--ghost"><Trash2 size={16} /> Excluir</button>}
          </div>
        </div>

        <div className="patients-callouts" data-onboarding="chart-alerts">
          <div className="patients-callout patients-callout--rose">
            <AlertTriangle size={18} className="tone-rose" />
            <div>
              <strong>Alergias documentadas</strong>
              <div className="patients-tags">
                {allergies.length === 0 && <span className="patients-muted">Nenhuma alergia relatada</span>}
                {allergies.map((item) => <span key={item.id} className="patients-tag patients-tag--rose">{item.description}</span>)}
              </div>
            </div>
          </div>
          <div className="patients-callout patients-callout--amber">
            <ShieldAlert size={18} className="tone-amber" />
            <div>
              <strong>Alertas clínicos</strong>
              <div className="patients-tags">
                {alerts.length === 0 && <span className="patients-muted">Sem alertas especiais ativos</span>}
                {alerts.map((item) => <span key={item.id} className="patients-tag patients-tag--amber">{item.description}</span>)}
              </div>
            </div>
          </div>
        </div>

        {archived && (
          <div className="patients-callout patients-callout--amber" style={{ marginBottom: '1rem' }}>
            <Archive size={18} className="tone-amber" />
            <div>
              <strong>Prontuário arquivado por {archiveLabel.toLowerCase()}</strong>
              <p className="patients-muted">{chart.archive_justification}</p>
              <p className="patients-muted">Por {chart.archived_by_name || 'usuário do sistema'} em {formatDay(chart.archived_at)}</p>
            </div>
          </div>
        )}

        <PatientTabBar tabs={tabs} currentTab={currentTab} onSelect={setActiveTab} />
      </section>

      {currentTab === 'vitals' && (
        <div>
          <div className="patients-section-head">
            <div>
              <h3>Parâmetros vitais atuais</h3>
              <p className="patients-muted">Registrado em {latest ? formatDateTime(latest.recorded_at) : 'sem medição'} por {latest?.recorded_by_name || '—'}</p>
            </div>
            {(canRegisterWeight || canRegisterHeight || canRegisterVitals) && !archived && (
              <div className="patients-section-actions" data-onboarding="chart-measure">
                {canRegisterWeight && (
                  <button type="button" className="patients-measure-btn" title="Registrar peso" aria-label="Registrar peso" onClick={() => setMeasureKind('peso')}>
                    <Weight size={16} />
                  </button>
                )}
                {canRegisterHeight && (
                  <button type="button" className="patients-measure-btn" title="Registrar altura" aria-label="Registrar altura" onClick={() => setMeasureKind('altura')}>
                    <Ruler size={16} />
                  </button>
                )}
                {canRegisterVitals && (
                  <button type="button" onClick={() => setVitalOpen(true)} className="patients-btn patients-btn--rose">
                    <Plus size={14} /> Nova medição
                  </button>
                )}
              </div>
            )}
          </div>
          <div data-onboarding="chart-vitals">
          <div className="patients-metrics patients-metrics--3">
            <article className="patients-metric">
              <div className="patients-metric-label">Peso <Weight size={16} className="tone-green" /></div>
              <p className="patients-metric-value">{peso ? formatBody(peso.value, 1) : '—'} <small>kg</small></p>
              <span>{peso ? `${formatDateTime(peso.recorded_at)} · ${peso.recorded_by_name || '—'}` : 'Sem registro'}</span>
            </article>
            <article className="patients-metric">
              <div className="patients-metric-label">Altura <Ruler size={16} className="tone-sky" /></div>
              <p className="patients-metric-value">{altura ? formatBody(altura.value, 2) : '—'} <small>m</small></p>
              <span>{altura ? `${formatDateTime(altura.recorded_at)} · ${altura.recorded_by_name || '—'}` : 'Sem registro'}</span>
            </article>
            <article className="patients-metric">
              <div className="patients-metric-label">IMC</div>
              <p className="patients-metric-value">{imc != null ? formatBody(imc, 1) : '—'} <small>kg/m²</small></p>
              <span>{imc != null ? 'Calculado com o último peso e a última altura' : 'Informe peso e altura'}</span>
            </article>
          </div>
          {(latest || latestGlucose) && (
            <div className="patients-metrics patients-metrics--6">
              <article className="patients-metric"><div className="patients-metric-label">Pressão arterial <HeartPulse size={16} className="tone-rose" /></div><p className="patients-metric-value">{latest?.systolic || '—'}/{latest?.diastolic || '—'} <small>mmHg</small></p></article>
              <article className="patients-metric"><div className="patients-metric-label">Freq. cardíaca <Heart size={16} className="tone-amber" /></div><p className="patients-metric-value tone-amber">{latest?.heart_rate || '—'} <small>bpm</small></p></article>
              <article className="patients-metric"><div className="patients-metric-label">Saturação O2 <Wind size={16} className="tone-sky" /></div><p className="patients-metric-value tone-sky">{latest?.oxygen_saturation || '—'}<small>%</small></p></article>
              <article className="patients-metric"><div className="patients-metric-label">Temperatura <Thermometer size={16} className="tone-rose" /></div><p className="patients-metric-value tone-rose">{latest?.temperature || '—'}<small>°C</small></p></article>
              <article className="patients-metric"><div className="patients-metric-label">Glicemia <Droplet size={16} className="tone-rose" /></div><p className="patients-metric-value">{latestGlucose?.blood_glucose || '—'} <small>mg/dL</small></p></article>
              <article className="patients-metric"><div className="patients-metric-label">Escala de dor <Smile size={16} className="tone-green" /></div><p className="patients-metric-value tone-green">{latest ? (latest.pain_scale ?? 0) : '—'} <small>/ 10</small></p></article>
            </div>
          )}
          </div>
          <div className="patients-evolution-layout">
            <div className="patients-evolution-calendar-inline">
              <Calendar
                mode="range"
                compact
                title="Filtrar aferições"
                subtitle="Início e fim"
                onRangeSelect={(start, end) => {
                  const bounds = evolutionBounds(start, end)
                  setVitalFrom(bounds.from)
                  setVitalTo(bounds.to)
                }}
              />
            </div>
            <VitalHistoryTable
              vitals={visibleVitals}
              canEdit={canEditVitals && !archived}
              summary={vitalFrom || vitalTo
                ? `${visibleVitals.length} de ${vitals.length} no período`
                : 'Rastreamento cronológico de enfermagem e plantão médico.'}
              emptyLabel={vitalFrom || vitalTo ? 'Nenhuma aferição neste período.' : 'Nenhuma aferição registrada.'}
              onSave={(vitalId, payload) => patientService.updateVital(id, vitalId, payload).then(() => loadPatient())}
              actions={(
                <>
                  <button
                    type="button"
                    ref={vitalCalendarRef}
                    className={`patients-evolution-calendar-toggle patients-icon-action finance-dashboard-period-btn finance-dashboard-period-btn--interactive${vitalFrom || vitalTo ? ' is-active' : ''}`}
                    onClick={() => setVitalCalendarOpen((open) => !open)}
                    aria-expanded={vitalCalendarOpen}
                    aria-label="Filtrar período"
                    title="Filtrar período"
                  >
                    <CalendarDays size={14} />
                  </button>
                  <CalendarRangePicker
                    open={vitalCalendarOpen}
                    onOpenChange={setVitalCalendarOpen}
                    anchorRef={vitalCalendarRef}
                    startValue={vitalFrom || null}
                    endValue={vitalTo || null}
                    title="Filtrar aferições"
                    subtitle="Início e fim"
                    selectSingleDay
                    onRangeChange={(start, end) => {
                      const from = start || ''
                      setVitalFrom(from)
                      setVitalTo(end || from)
                    }}
                    onClear={() => {
                      setVitalFrom('')
                      setVitalTo('')
                    }}
                  />
                  <button type="button" onClick={downloadVitals} className="patients-icon-action finance-dashboard-period-btn finance-dashboard-period-btn--interactive" aria-label="Baixar relatório" title="Baixar">
                    <CloudDownload size={14} />
                  </button>
                  <button type="button" onClick={printVitals} className="patients-icon-action finance-dashboard-period-btn finance-dashboard-period-btn--interactive" aria-label="Imprimir relatório" title="Imprimir">
                    <Printer size={14} />
                  </button>
                </>
              )}
            />
          </div>
        </div>
      )}

      {currentTab === 'medications' && (
        <MedicationsTab
          patientId={chart.id}
          archived={archived}
          canPrescribe={canPrescribe && !archived}
          canAdminister={canAdminister && !archived}
          allergies={allergies}
          prescribeTick={prescribeTick}
          onChanged={loadPatient}
        />
      )}

      {currentTab === 'nutrition' && (
        <NutritionTab
          patientId={chart.id}
          patientName={chart.name}
          archived={archived}
          canEdit={canNutrition && !archived}
          canRecordWater={canRecordWater && !archived}
          canSeePrescriptions={canNutritionPrescriptions}
          canCreatePrescriptions={canCreateNutritionPrescriptions && !archived}
          canRecordMeal={canRecordMeal && !archived}
        />
      )}

      {currentTab === 'contacts' && (
        <PatientContactsTab
          chart={chart}
          archived={archived}
          canEdit={canEditContacts}
          onSaved={loadPatient}
        />
      )}

      {currentTab === 'payments' && (
        <div>
          <div className="patients-metrics patients-metrics--3">
            <article className="patients-metric"><div className="patients-metric-label">Total faturado</div><p className="patients-metric-value">{formatMoney(totalBilled)}</p><span>{charges.length} lançamentos</span></article>
            <article className="patients-metric"><div className="patients-metric-label tone-green">Total quitado</div><p className="patients-metric-value tone-green">{formatMoney(totalPaid)}</p><span>Recebido na conta do extrato</span></article>
            <article className="patients-metric"><div className="patients-metric-label tone-amber">Saldo pendente</div><p className="patients-metric-value tone-amber">{formatMoney(totalPending)}</p><span>Aguardando faturamento</span></article>
          </div>
          <div className="patients-table-card">
            <div className="patients-table-head">
              <div>
                <h3>Lançamentos financeiros</h3>
                <p className="patients-muted">Diárias, materiais, medicamentos e procedimentos deste prontuário.</p>
              </div>
              {canFinancial && !archived && (
                <button type="button" onClick={() => setPaymentOpen(true)} className="patients-btn patients-btn--green">
                  <Plus size={14} /> Novo lançamento
                </button>
              )}
            </div>
            <div className="patients-table-scroll">
              <table className="patients-table">
                <thead>
                  <tr>
                    <th>Data</th><th>Guia</th><th>Descrição</th><th>Categoria</th><th>Conta</th><th>Valor</th><th>Status</th><th className="is-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.length === 0 && <tr><td colSpan={8}>Nenhum lançamento financeiro.</td></tr>}
                  {charges.map((item) => (
                    <tr key={item.id}>
                      <td>{formatDay(item.due_date)}</td>
                      <td className="tone-green is-strong">{item.invoice_number || item.receivable_number || '—'}</td>
                      <td className="is-strong">{item.description}</td>
                      <td><span className="patients-tag patients-tag--slate">{labelOf(CHARGE_CATEGORIES, item.category)}</span></td>
                      <td>{item.financial_account_name ? `${item.financial_account_name}${item.financial_account_code ? ` (${item.financial_account_code})` : ''}` : '—'}</td>
                      <td className="is-strong">{formatMoney(item.amount)}</td>
                      <td><span className={badgeClass(item.status)}>{labelOf(CHARGE_STATUSES, item.status)}</span></td>
                      <td className="is-right">
                        <div className="patients-row-actions">
                        {canFinancial && !archived && (
                          <button type="button" onClick={() => run(() => patientService.updateChargeStatus(id, item.id, item.status === 'pago' ? 'pendente' : 'pago'))} className="patients-btn patients-btn--ghost patients-btn--compact">
                            {item.status === 'pago' ? 'Marcar pendente' : 'Marcar pago'}
                          </button>
                        )}
                        {item.receivable_number && (
                          <a
                            className="patients-icon-btn"
                            href={`/erp/financeiro/contas-receber?search=${encodeURIComponent(item.receivable_number)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`Abrir ${item.receivable_number}`}
                          >
                            <ExternalLink size={15} />
                          </a>
                        )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {currentTab === 'evolutions' && (
        <div>
          <div className="patients-section-head patients-evolution-head">
            <div>
              <h3>Evoluções clínicas</h3>
              <p className="patients-muted">
                {evolutionFrom || evolutionTo
                  ? `${visibleEvolutions.length} de ${evolutions.length} no período`
                  : 'Metodologia SOAP vinculada a um usuário do ERP.'}
              </p>
            </div>
            <div className="patients-section-actions">
              <button
                type="button"
                ref={evolutionCalendarRef}
                className={`patients-evolution-calendar-toggle finance-dashboard-period-btn finance-dashboard-period-btn--interactive${evolutionFrom || evolutionTo ? ' is-active' : ''}`}
                onClick={() => setEvolutionCalendarOpen((open) => !open)}
                aria-expanded={evolutionCalendarOpen}
              >
                <CalendarDays size={14} /> Calendário
              </button>
              <CalendarRangePicker
                open={evolutionCalendarOpen}
                onOpenChange={setEvolutionCalendarOpen}
                anchorRef={evolutionCalendarRef}
                startValue={evolutionFrom || null}
                endValue={evolutionTo || null}
                title="Filtrar evoluções"
                subtitle="Início e fim"
                selectSingleDay
                onRangeChange={(start, end) => {
                  const from = start || ''
                  setEvolutionFrom(from)
                  setEvolutionTo(end || from)
                }}
                onClear={() => {
                  setEvolutionFrom('')
                  setEvolutionTo('')
                }}
              />
              {canPrintEvolutions && (
                <button type="button" onClick={downloadEvolutions} className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive">
                  <CloudDownload size={14} /> Baixar
                </button>
              )}
              {canPrintEvolutions && (
                <button type="button" onClick={printEvolutions} className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive">
                  <Printer size={14} /> Imprimir
                </button>
              )}
              {canEvolutions && !archived && (
                <button type="button" onClick={() => setEvolutionOpen(true)} className="patients-btn patients-btn--primary">
                  <Plus size={14} /> Nova evolução
                </button>
              )}
            </div>
          </div>
          <div className="patients-evolution-layout">
            <div className="patients-evolution-calendar-inline">
            <Calendar
              mode="range"
              compact
              title="Filtrar evoluções"
              subtitle="Início e fim"
              onRangeSelect={(start, end) => {
                const bounds = evolutionBounds(start, end)
                setEvolutionFrom(bounds.from)
                setEvolutionTo(bounds.to)
              }}
            />
            </div>
            <div className="patients-stack">
            {evolutions.length === 0 && <p className="patients-muted">Nenhuma evolução registrada.</p>}
            {evolutions.length > 0 && visibleEvolutions.length === 0 && (
              <p className="patients-muted">Nenhuma evolução neste período.</p>
            )}
            {visibleEvolutions.map((item) => (
              <article key={item.id} className="patients-panel">
                <div className="patients-section-head">
                  <div className="patients-inline">
                    <div className="patients-avatar patients-avatar--accent"><Stethoscope size={18} /></div>
                    <div>
                      <h3>{item.author_name}</h3>
                      <p className="patients-muted tone-green">{item.role || 'Profissional'} · {item.registration_number || 'sem registro'}</p>
                    </div>
                  </div>
                  <div className="patients-evolution-when">
                    <strong>{formatDay(item.evolution_date)} às {String(item.evolution_time || '').slice(0, 5)}</strong>
                    <span>Lançado em {formatDateTime(item.created_at)}</span>
                    {item.cid && <span>{item.cid}</span>}
                  </div>
                </div>
                <div className="patients-soap">
                  <article><span>S — Subjetivo</span><p>{item.subjective || '—'}</p></article>
                  <article><span>O — Objetivo</span><p>{item.objective || '—'}</p></article>
                  <article><span>A — Avaliação</span><p>{item.assessment || '—'}</p></article>
                  <article className="is-plan"><span>P — Plano</span><p>{item.plan || '—'}</p></article>
                </div>
              </article>
            ))}
            </div>
          </div>
        </div>
      )}

      {currentTab === 'notes' && <NotesTab patientId={chart.id} canManage={canRecordNotes && !archived} />}

      {currentTab === 'therapy' && (
        <TherapyTab patientId={chart.id} archived={archived} />
      )}

      {currentTab === 'documents' && (
        <div>
          <div className="patients-section-head">
            <div>
              <h3>Documentação do paciente</h3>
              <p className="patients-muted">Termos, laudos, guias e demais arquivos deste prontuário.</p>
            </div>
          </div>
          {canDocuments && !archived && (
            <form onSubmit={uploadDocument} className="patients-upload">
              <div className="patients-upload-grid">
                <div className="patients-field">
                  <label htmlFor="patient-doc-title">Título</label>
                  <input
                    id="patient-doc-title"
                    required
                    value={documentTitle}
                    onChange={(e) => setDocumentTitle(e.target.value)}
                    placeholder="Ex: Termo de consentimento"
                  />
                </div>
                <div className="patients-field">
                  <label htmlFor="patient-doc-file">Arquivo</label>
                  <input
                    id="patient-doc-file"
                    ref={documentInput}
                    className="patients-file"
                    required
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,application/pdf,image/*"
                    onChange={(e) => setDocumentFile(e.target.files?.[0] || null)}
                  />
                </div>
              </div>
              <div className="patients-upload-actions">
                <button type="submit" className="patients-btn patients-btn--primary">
                  <Plus size={14} /> Enviar documento
                </button>
              </div>
            </form>
          )}
          <div className="patients-table-card">
            <div className="patients-table-scroll">
              <table className="patients-table">
                <thead>
                  <tr>
                    <th>Título</th><th>Arquivo</th><th>Enviado por</th><th>Data</th><th className="is-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 && <tr><td colSpan={5}>Nenhum documento enviado.</td></tr>}
                  {documents.map((item) => (
                    <tr key={item.id}>
                      <td className="is-strong">{item.title}</td>
                      <td>{item.file_name}</td>
                      <td>{item.uploaded_by_name || '—'}</td>
                      <td>{formatDay(item.created_at)}</td>
                      <td className="is-right">
                        <div className="patients-row-actions">
                          {item.file_url && (
                            <button type="button" onClick={() => downloadDocumentFile(item)} className="patients-btn patients-btn--ghost patients-btn--compact" title="Baixar">
                              <Download size={14} /> Baixar
                            </button>
                          )}
                          {canDocuments && !archived && (
                            <button type="button" onClick={() => run(() => patientService.deleteDocument(id, item.id))} className="patients-btn patients-btn--ghost patients-btn--compact" title="Excluir">
                              <Trash2 size={14} /> Excluir
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <NewPatientModal
        isOpen={editOpen}
        patient={chart}
        onClose={() => setEditOpen(false)}
        onCreated={(data) => { if (data) setChart(data) }}
      />
      <AddVitalSignsModal isOpen={vitalOpen} patientId={chart.id} patientName={chart.name} onClose={() => setVitalOpen(false)} onAdded={loadPatient} />
      <AddBodyMeasureModal
        isOpen={Boolean(measureKind)}
        kind={measureKind || 'peso'}
        patientId={chart.id}
        patientName={chart.name}
        onClose={() => setMeasureKind(null)}
        onAdded={loadPatient}
      />
      <ArchivePatientModal isOpen={archiveOpen} patientId={chart.id} patientName={chart.name} onClose={() => setArchiveOpen(false)} onArchived={loadPatient} />
      <PatientAccessModal
        isOpen={accessOpen}
        patientId={chart.id}
        viewers={chart.viewers || []}
        onClose={() => setAccessOpen(false)}
        onSaved={(data) => {
          if (!data) navigate('/erp/pacientes')
          else setChart(data)
        }}
      />
      <AddPaymentModal isOpen={paymentOpen} patientId={chart.id} patientName={chart.name} onClose={() => setPaymentOpen(false)} onAdded={loadPatient} />
      <AddEvolutionModal isOpen={evolutionOpen} patientId={chart.id} onClose={() => setEvolutionOpen(false)} onAdded={loadPatient} />
      {((canSeeVitalShortcut && canRegisterVitals) || canRecordWater || canRecordNotes) && !archived && (
        <div className="patients-quick-fabs">
          {canSeeVitalShortcut && canRegisterVitals && (
            <button
              type="button"
              className="patients-note-fab is-vitals"
              data-onboarding="chart-fab-vitals"
              aria-label="Aferir sinais"
              title="Aferir sinais"
              onClick={() => setVitalOpen(true)}
            >
              <Activity size={22} />
            </button>
          )}
          {canRecordWater && (
            <NutritionWaterButton
              variant="fab"
              tourAnchor="chart-fab-water"
              patientId={chart.id}
              patientName={chart.name}
              date={todayInputValue()}
              onAdded={() => window.dispatchEvent(new CustomEvent('wfmed-nutrition-water'))}
            />
          )}
          {canRecordNotes && (
            <button
              type="button"
              className="patients-note-fab"
              data-onboarding="chart-fab-notes"
              aria-label="Nota rápida"
              title="Nota rápida"
              onClick={() => setNotesOpen(true)}
            >
              <StickyNotePlus size={22} />
            </button>
          )}
        </div>
      )}
      <OnboardingTour
        tourId={CHART_TOUR_ID}
        steps={CHART_ONBOARDING_STEPS}
        userId={user?.id}
        ready
        replayToken={tourReplay}
      />
      <ShiftNoteSheet
        open={notesOpen && canRecordNotes && !archived}
        patientId={chart.id}
        patientName={chart.name}
        onClose={() => setNotesOpen(false)}
      />
      {shiftBlocked && createPortal(
        <div className="patients-shift-veil" role="dialog" aria-modal="true" aria-label="Plantão necessário">
          <div className="patients-shift-veil-card">
            <p>
              {shiftBlockMessage
                || (shiftState.open && !shiftLinkedHere
                  ? `Seu plantão está vinculado a ${shiftState.open.patient_name || 'outro paciente'}.`
                  : 'Inicie o plantão para registrar neste prontuário.')}
            </p>
            <span>
              {shiftState.open && !shiftLinkedHere
                ? 'Encerre esse plantão antes de registrar aqui.'
                : 'O botão Plantão continua no topo da tela.'}
            </span>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default PatientRecordPage
